import { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
    getAllColors,
    getDesignFonts,
    getLittlePredesigns,
    getPrintsDetails,
} from "../../services/api";
import {
    adaptAllColorsResponse,
    adaptPrintDetailsResponse,
} from "../../utils/designerAdapters";
import { setDesignFonts } from "../../utils/designerUtils";

const normalizePredesignAsset = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (raw.startsWith("http")) {
        try {
            const url = new URL(raw);
            return `${url.pathname}${url.search}`.toLowerCase();
        } catch (_error) {
            return raw.toLowerCase();
        }
    }
    return raw.toLowerCase();
};

const tokenizePredesignAsset = (value) => {
    const normalized = normalizePredesignAsset(value);
    if (!normalized) return "";
    const withoutQuery = normalized.split("?")[0];
    const baseName = withoutQuery.split("/").filter(Boolean).pop() || "";
    const withoutExt = baseName.replace(/\.[a-z0-9]+$/i, "");
    const normalizedWords = withoutExt
        .replace(/[_-]+/g, " ")
        .replace(/\b(copy|copia|dup|duplicado|final|new|nuevo|ok|v\d+)\b/g, " ")
        .replace(/\b(chico|chica|small|mini|grande|large|maxi|mediano|mediana)\b/g, " ")
        .replace(/\b(\d{1,3}\s?cm|a3|a4|a5|x1|x2|x3)\b/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    const trimmedTrailingNumbers = normalizedWords.replace(/\b\d{1,4}\b$/g, "").trim();
    return trimmedTrailingNumbers || normalizedWords || withoutExt;
};

const buildPredesignSignature = (print) => {
    const full = normalizePredesignAsset(print?.img_full || print?.img_url || "");
    const preview = normalizePredesignAsset(print?.img || "");
    const icon = normalizePredesignAsset(print?.icon_url || print?.img_url || "");
    const canonicalToken = tokenizePredesignAsset(print?.img_full || print?.img || print?.icon_url || print?.img_url || "");
    if (canonicalToken) return `token:${canonicalToken}`;
    if (full) return `full:${full}`;
    if (preview) return `preview:${preview}`;
    if (icon) return `icon:${icon}`;
    return "";
};

const dedupePredesigns = (items = []) => {
    const bySignature = new Map();
    (items || []).forEach((item) => {
        const signature = buildPredesignSignature(item);
        if (!signature) return;
        const current = bySignature.get(signature);
        if (!current) {
            bySignature.set(signature, item);
            return;
        }
        const currentHasFull = !!String(current?.img_full || "").trim();
        const nextHasFull = !!String(item?.img_full || "").trim();
        if (nextHasFull && !currentHasFull) {
            bySignature.set(signature, item);
            return;
        }
        const currentId = Number(current?.id || 0);
        const nextId = Number(item?.id || 0);
        if (nextId > currentId) {
            bySignature.set(signature, item);
        }
    });
    return Array.from(bySignature.values()).sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
};

const useDesignerRemoteData = ({
    isDesignerOpen,
    isPrintsStep,
    selectedPrints,
}) => {
    const [allColors, setAllColors] = useState([]);
    const [, setLoadingAllColors] = useState(false);
    const [printDetails, setPrintDetails] = useState({});
    const [littlePredesigns, setLittlePredesigns] = useState([]);
    const [littlePredesignsLoading, setLittlePredesignsLoading] = useState(false);
    const [littlePredesignsLoaded, setLittlePredesignsLoaded] = useState(false);
    // Solo para forzar re-render cuando llegan las fuentes del ABM (la data real vive en el
    // cache de módulo de designerUtils, que leen los renderers y selectores sincrónicamente).
    const [designFontsLoaded, setDesignFontsLoaded] = useState(false);
    const warnedRef = useRef({
        printDetails: false,
        predesigns: false,
        colors: false,
    });

    const showWarningToast = useCallback((title, text) => {
        Swal.fire({
            toast: true,
            position: "top-end",
            icon: "warning",
            title,
            text,
            showCloseButton: true,
            showConfirmButton: false,
            timer: 3500,
            timerProgressBar: true,
        });
    }, []);

    const preloadPrintDetailsByIds = useCallback(async (ids) => {
        const cleanIds = Array.from(new Set((ids || []).filter(Boolean))).filter((id) => !printDetails[id]);
        if (!cleanIds.length) return;
        try {
            const response = await getPrintsDetails(cleanIds);
            setPrintDetails((prev) => {
                const next = { ...prev };
                adaptPrintDetailsResponse(response).forEach((print) => {
                    next[print.id] = print;
                });
                return next;
            });
        } catch (_error) {
            if (!warnedRef.current.printDetails) {
                warnedRef.current.printDetails = true;
                showWarningToast("No se cargaron algunos detalles de diseños", "Podés seguir diseñando, pero puede faltar información de algunos diseños.");
            }
        }
    }, [printDetails, showWarningToast]);

    const loadLittlePredesigns = useCallback(async ({ force = false } = {}) => {
        if (littlePredesignsLoading) return;
        if (littlePredesignsLoaded && !force) return;
        setLittlePredesignsLoading(true);
        try {
            const response = await getLittlePredesigns();
            const items = adaptPrintDetailsResponse(response)
                .filter((print) => {
                    const preview = String(print?.img || print?.icon_url || print?.img_url || "").trim();
                    const full = String(print?.img_full || print?.img_url || "").trim();
                    return !!(preview || full);
                })
                .map((print) => ({
                    ...print,
                    img: print?.img || print?.icon_url || print?.img_url || null,
                    img_full: print?.img_full || print?.img_url || null,
                }));
            setLittlePredesigns(dedupePredesigns(items));
            setLittlePredesignsLoaded(true);
        } catch (_error) {
            if (!warnedRef.current.predesigns) {
                warnedRef.current.predesigns = true;
                showWarningToast("No se pudieron cargar los Diseños Little", "Intentá nuevamente en unos segundos.");
            }
        } finally {
            setLittlePredesignsLoading(false);
        }
    }, [littlePredesignsLoading, littlePredesignsLoaded, showWarningToast]);

    useEffect(() => {
        if (!isPrintsStep) return;
        loadLittlePredesigns();
    }, [isPrintsStep, loadLittlePredesigns]);

    useEffect(() => {
        if (!isDesignerOpen) return;
        if (!allColors.length) {
            setLoadingAllColors(true);
            getAllColors()
                .then((response) => {
                    setAllColors(adaptAllColorsResponse(response));
                })
                .catch(() => {
                    if (!warnedRef.current.colors) {
                        warnedRef.current.colors = true;
                        showWarningToast("No se pudieron cargar colores", "Probá recargar la vista para reintentar.");
                    }
                })
                .finally(() => setLoadingAllColors(false));
        }
    }, [isDesignerOpen, allColors.length, showWarningToast]);

    useEffect(() => {
        const ids = Object.values(selectedPrints || {})
            .map((selection) => selection?.print?.id)
            .filter(Boolean);
        if (!ids.length) return;
        preloadPrintDetailsByIds(ids);
    }, [selectedPrints, preloadPrintDetailsByIds]);

    // Tipografías del ABM de V4: se cargan una vez al abrir el diseñador. Si falla, el cache queda
    // vacío y todo cae a la tabla fija (MASTER_PRINT_FONTS) → comportamiento de hoy, sin romper.
    useEffect(() => {
        if (!isDesignerOpen || designFontsLoaded) return;
        let cancelled = false;
        getDesignFonts()
            .then((response) => {
                if (cancelled) return;
                setDesignFonts(response?.fonts || response || []);
                setDesignFontsLoaded(true); // re-render para que selectores/renderers tomen el cache
            })
            .catch(() => { /* silencioso: queda la tabla fija */ });
        return () => { cancelled = true; };
    }, [isDesignerOpen, designFontsLoaded]);

    return {
        allColors,
        printDetails,
        littlePredesigns,
        littlePredesignsLoading,
        littlePredesignsLoaded,
        designFontsLoaded,
        loadLittlePredesigns,
        preloadPrintDetailsByIds,
    };
};

export default useDesignerRemoteData;
